<template>
    <div class="automation-department-permissions-tab">
        <div class="search-section">
            <a-input-search
                v-model="searchText"
                :placeholder="$t('message.search')"
                class="search-input"
                @search="onSearch"
            />
        </div>

        <div class="scopes-table">
            <div class="scopes-table-header">
                <div class="header-cell target-header">
                    {{ $t('automation.permissions.targetHeader') }}
                </div>
                <div 
                    v-for="group in scopeGroups" 
                    :key="group.id" 
                    class="header-group"
                >
                    <div class="group-title">
                        <i class="material-icons">{{ group.icon }}</i>
                        {{ $t(group.titleKey) }}
                    </div>
                    <div class="group-scopes">
                        <div 
                            v-for="scope in group.scopes" 
                            :key="scope"
                            class="scope-header"
                            v-tippy="{ content: getScopeDescription(scope) }"
                        >
                            {{ getScopeLabelText(scope) }}
                        </div>
                    </div>
                </div>
            </div>

            <div class="scopes-table-body">
                <!-- Aktif departmanlar -->
                <div v-if="filteredActiveDepartments.length > 0" class="group-section">
                    <div class="group-header active-group">
                        <i class="material-icons">check_circle</i>
                        <span>{{ $t('automation.permissions.authorizedDepartments') }} ({{ filteredActiveDepartments.length }})</span>
                    </div>
                    <AutomationDepartmentPermissionRow
                        v-for="(deptItem, index) in filteredActiveDepartments"
                        :key="`active-dept-${deptItem.department._id}`"
                        :department="deptItem.department"
                        :target="deptItem.target"
                        :target-index="deptItem.targetIndex"
                        :scope-groups="scopeGroups"
                        :users="users"
                        :permission-targets="permissionTargets"
                        :is-active="true"
                        :is-odd="index % 2 === 1"
                        @toggle-scope="onToggleScope"
                        @deactivate="onDeactivate"
                    />
                </div>

                <!-- İnaktif departmanlar -->
                <div v-if="filteredInactiveDepartments.length > 0" class="group-section">
                    <div class="group-header inactive-group">
                        <i class="material-icons">radio_button_unchecked</i>
                        <span>{{ $t('automation.permissions.otherDepartments') }} ({{ filteredInactiveDepartments.length }})</span>
                    </div>
                    <AutomationDepartmentPermissionRow
                        v-for="(department, index) in filteredInactiveDepartments"
                        :key="`inactive-dept-${department._id}`"
                        :department="department"
                        :target="null"
                        :target-index="-1"
                        :scope-groups="scopeGroups"
                        :users="users"
                        :permission-targets="permissionTargets"
                        :is-active="false"
                        :is-odd="index % 2 === 1"
                        @activate="onActivate"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import AutomationDepartmentPermissionRow from "./AutomationDepartmentPermissionRow.vue";
import { AUTOMATION_SCOPE_LABELS } from "../../../../../utils/AutomationPermissionScopes";

export default Vue.extend({
    name: "AutomationDepartmentPermissionsTab",
    components: {
        AutomationDepartmentPermissionRow,
    },
    props: {
        departments: {
            type: Array,
            required: true,
        },
        users: {
            type: Array,
            required: true,
        },
        permissionTargets: {
            type: Array,
            required: true,
        },
        scopeGroups: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            searchText: "",
        };
    },
    computed: {
        activeDepartments: function() {
            var self = this;
            var active = [];
            this.permissionTargets.forEach(function(target, index) {
                if (target.target === "department" && target.department) {
                    var dept = self.departments.find(function(d) {
                        return d._id.toString() === target.department.toString();
                    });
                    if (dept) {
                        active.push({
                            department: dept,
                            target: target,
                            targetIndex: index,
                        });
                    }
                }
            });
            return active;
        },
        inactiveDepartments: function() {
            var self = this;
            var activeDeptIds = this.activeDepartments.map(function(item) {
                return item.department._id.toString();
            });
            return this.departments.filter(function(d) {
                return !activeDeptIds.includes(d._id.toString());
            });
        },
        filteredActiveDepartments: function() {
            var self = this;
            if (!this.searchText) return this.activeDepartments;
            var search = this.searchText.toLowerCase();
            return this.activeDepartments.filter(function(item) {
                return item.department.name.toLowerCase().includes(search);
            });
        },
        filteredInactiveDepartments: function() {
            var self = this;
            if (!this.searchText) return this.inactiveDepartments;
            var search = this.searchText.toLowerCase();
            return this.inactiveDepartments.filter(function(d) {
                return d.name.toLowerCase().includes(search);
            });
        },
    },
    methods: {
        onSearch: function(value) {
            this.searchText = value;
        },
        getScopeLabelText: function(scope) {
            var label = AUTOMATION_SCOPE_LABELS[scope] || { labelKey: scope };
            return this.$t(label.labelKey);
        },
        getScopeDescription: function(scope) {
            var label = AUTOMATION_SCOPE_LABELS[scope] || { descriptionKey: scope };
            return this.$t(label.descriptionKey);
        },
        onToggleScope: function(payload) {
            this.$emit("toggle-scope", payload);
        },
        onActivate: function(deptId) {
            this.$emit("activate-target", {
                targetType: "department",
                targetId: deptId,
            });
        },
        onDeactivate: function(deptId) {
            var self = this;
            var targetIndex = this.permissionTargets.findIndex(function(t) {
                return t.target === "department" && t.department && t.department.toString() === deptId.toString();
            });
            if (targetIndex !== -1) {
                this.$emit("activate-target", {
                    targetType: "department",
                    targetId: deptId,
                });
            }
        },
    },
});
</script>

<style scoped lang="scss">
.automation-department-permissions-tab {
    .search-section {
        padding: 15px;
        background: white;
        border-bottom: 1px solid var(--color-border-default);

        .search-input {
            width: 100%;
            max-width: 400px;
        }
    }

    .scopes-table {
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        overflow: hidden;

        .scopes-table-header {
            display: flex;
            background: var(--color-bg-neutral-default);
            border-bottom: 2px solid var(--color-border-default);
            position: sticky;
            top: 0;
            z-index: 10;

            .header-cell {
                padding: 12px;
                font-weight: 600;
                border-right: 1px solid var(--color-border-default);

                &.target-header {
                    width: 250px;
                    min-width: 250px;
                    flex-shrink: 0;
                }
            }

            .header-group {
                flex: 1;
                border-right: 1px solid var(--color-border-default);

                &:last-child {
                    border-right: none;
                }

                .group-title {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 5px;
                    padding: 8px;
                    background: var(--color-bg-default);
                    border-bottom: 1px solid var(--color-border-default);
                    font-weight: 600;
                    font-size: 13px;

                    .material-icons {
                        font-size: 16px;
                    }
                }

                .group-scopes {
                    display: flex;

                    .scope-header {
                        flex: 1;
                        padding: 8px 4px;
                        text-align: center;
                        font-size: 12px;
                        font-weight: 500;
                        border-right: 1px solid var(--color-border-subdued);
                        cursor: help;

                        &:last-child {
                            border-right: none;
                        }
                    }
                }
            }
        }

        .scopes-table-body {
            max-height: 500px;
            overflow-y: auto;

            .group-section {
                .group-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 12px;
                    background: #fafafa;
                    border-bottom: 2px solid var(--color-border-default);
                    font-weight: 600;
                    font-size: 14px;

                    &.active-group {
                        background: #e6f7ff;
                        color: #0050b3;

                        .material-icons {
                            color: #52c41a;
                        }
                    }

                    &.inactive-group {
                        background: #f5f5f5;
                        color: #666;

                        .material-icons {
                            color: #999;
                        }
                    }
                }
            }
        }
    }
}
</style>
